/*******************************************************************************
 * Copyright     : Copyright (c)  2025 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { useState, useEffect, useCallback } from "react";

//@Packages
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { 
  Grid, 
  Card, 
  Button, 
  Banner, 
  Select, 
  SelectOption, 
  TextArea,
  CardHeader,
  CardContent
} from "@forge/common";

//@Models
import { CustomAppProps } from "../../../../models/softwaredetails";

//@Store
import { RootState } from "../../../../store";

//@Actions
import { setSoftwareVersions, setAppConfig } from "../../../../actions";

//@Utils
import { SOFTWARE_CONSTANTS } from "../../../../utils/constants";


const FcCustomApp: React.FC<CustomAppProps> = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Redux state
  const { versions, appConfig } = useSelector((state: RootState) => state.global.softwareDetails || {});

  // Local state
  const [showWarningMessage, setShowWarningMessage] = useState(false);
  const [payload, setPayload] = useState(props.payload || '');
  const [version, setVersion] = useState(props.version || '');
  const [fields, setFields] = useState<any>({});
  const [errors, setErrors] = useState<any>({});
  const [formIsValid, setFormIsValid] = useState(true);

  let versionArray: any[] = [];
  let formData: any = {};

  // Initialize component
  useEffect(() => {
    setPayload("");
    versionList();
    handleValidation();
  }, []);

  useEffect(() => {
    if (props.payload !== payload) {
      setPayload(JSON.stringify(props.payload, null, 2));
      handleChange(props.payload, "payload");
      handleValidation();
    }
  }, [props.payload]);

  useEffect(() => {
    handleValidation();
  }, [fields]);

  // Update local state when Redux store changes
  useEffect(() => {
    if (appConfig) {
      setPayload(JSON.stringify(appConfig, null, 2));
      handleChange(appConfig, "payload");
      handleValidation();
    }
  }, [appConfig]);

  const versionList = (): void => {
    if (props.getSoftwareVersions) {
      props.getSoftwareVersions(props.softwareKey);
    }
  };

  /*
    - Fetch input version list from Redux store.
  */
  const getInputVersion = () => {
    versionArray = [];
    if (versions && versions.length > 0) {
      for (let i = 0; i < versions.length; i++) {
        versionArray.push({
          value: versions[i].version,
          text: versions[i].version
        });
      }
    }
  };

  /*
    - On version selection fetch payload.
  */
  const onVersionSelection = (selectedVersion: any) => {
    setVersion(selectedVersion);
    setPayload("Loading Payload..");
    handleChange(selectedVersion, "version");
    
    if (props.getAppConfig) {
      props.getAppConfig(props.softwareKey, selectedVersion);
    }
  };

  /*
    - Render version dropdown for the form.
  */
  const renderVersion = () => {
    getInputVersion();
    
    return (
      <div className="app-form-row grid grid-cols-2 gap-4">
        <div className="app-form-elements">
          <label className="input-label-message">Select Version</label>
        </div>
        <div className="app-form-elements">
          <Select
            id="version-select"
            value={version}
            onChange={(value) => onVersionSelection(value)}
            placeholder={versionArray.length > 0 ? "Select Version" : "Loading Version.."}
            disabled={versionArray.length === 0}
            className="app-form-select"
          >
            {versionArray.map((versionItem: any) => (
              <SelectOption
                key={versionItem.value}
                value={versionItem.value}
                label={versionItem.text}
              />
            ))}
          </Select>
          {errors["version"] && (
            <span className="error-msg">{errors["version"]}</span>
          )}
        </div>
      </div>
    );
  };

  /*
    - Render payload for the form.
  */
  const renderPayload = () => {
    return (
      <div className="app-form-row grid grid-cols-2 gap-4">
        <div className="app-form-elements">
          <label className="input-label-message">Payload</label>
        </div>
        <div className="app-form-elements">
          <TextArea
            id="payload-textarea"
            value={payload}
            className="app-form-input"
            disabled={
              payload === "Loading Payload.." || 
              versionArray.length === 0 ||
              version === ""
            }
            onChange={(e) => {
              const value = e.target.value;
              setPayload(value);
              handleChange(value, "payload");
              handleValidation();
            }}
            rows={10}
          />
          {errors["payload"] && (
            <span className="error-msg">{errors["payload"]}</span>
          )}
        </div>
      </div>
    );
  };

  /*
    - Render the buttons for the form.
  */
  const renderFormCommands = () => {
    const saveButton = (
      <Button 
        variant="primary" 
        size="regular"
        onClick={() => submit()}
        disabled={!formIsValid || payload === "Loading Payload.."}
      >
        {SOFTWARE_CONSTANTS.submit}
      </Button>
    );

    if (props.cancelEnabled) {
      return [
        <Button 
          key="cancel"
          variant="secondary" 
          size="regular"
          onClick={() => cancel()}
        >
          {SOFTWARE_CONSTANTS.cancel}
        </Button>,
        saveButton
      ];
    }
    return [saveButton];
  };

  /*
      - Handle the submit of form
  */
  const submit = () => {
    if (handleValidation()) {
      formData["appVersion"] = version;
      formData["payLoad"] = payload;
      if (props.onSubmit) {
        props.onSubmit(formData);
      }
    }
  };

  /*
    - Handle the cancel of form
  */
  const cancel = () => {
    if (props.onCancel) {
      props.onCancel(formData);
    }
  };

  /*
    - Handle change event
  */
  const handleChange = (value: any, field: any) => {
    const newFields = { ...fields };
    newFields[field] = value;
    setFields(newFields);
  };

  /*
    - Handle form validation
  */
  const handleValidation = () => {
    const newErrors: any = {};
    let isValid = true;

    // version
    if (!fields["version"]) {
      isValid = false;
      newErrors["version"] = "Cannot be blank";
    }

    // payload
    if (!fields["payload"]) {
      isValid = false;
      newErrors["payload"] = "Cannot be blank";
    }

    if (fields["payload"]) {
      try {
        (typeof (fields["payload"]) === "string") ? JSON.parse(fields["payload"]) : JSON.parse(JSON.stringify(fields["payload"]));
      } catch (e) {
        isValid = false;
        newErrors["payload"] = "Please enter valid JSON";
      }
    }

    setErrors(newErrors);
    setFormIsValid(isValid);
    return isValid;
  };

  /*
    - Display notification.
  */
  const displayNotification = () => {
    if (showWarningMessage && props.notificationProps) {
      // Map error to critical for Banner component
      const bannerType = props.notificationProps.severity === "error" ? "critical" : (props.notificationProps.severity || "warning");
      
      return (
        <div className="app-form-notification">
          <Banner
            variant="inline"
            type={bannerType}
            title={props.notificationProps.title}
            description={props.notificationProps.detailsText}
            canClose={true}
            onClose={closeNotification}
          />
        </div>
      );
    }
  };

  const closeNotification = () => {
    setShowWarningMessage(false);
  };

  return (
    <React.Fragment>
      <Card>
        <CardHeader className="app-form-header">{props.title}</CardHeader>
        <CardContent className="app-form-content">
          <div className="app-form-sub-header">{props.subTitle}</div>
          {displayNotification()}
          <div className="app-form-content">
            {renderVersion()}
            {renderPayload()}
          </div>
        </CardContent>
        <div className="app-form-footer">
          <div className="footer-buttons">
            {renderFormCommands()}
          </div>
        </div>
      </Card>
    </React.Fragment>
  );
};

export default FcCustomApp;